import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, input, effect, ElementRef, viewChild, inject, Signal, signal } from '@angular/core';
import { extend, injectStore, NgtArgs } from 'angular-three';
import { ArService } from '../services/ar.service';
import { ConfettiService } from '@kanek/shared-ui';
import { AudioService } from '@kanek/shared-ui';
import { NgtsHTML, NgtsHTMLContent, NgtsHTMLOptions } from 'angular-three-soba/misc';
import { NgtsOrbitControls } from 'angular-three-soba/controls';
import * as THREE from 'three';
import {
  BoxGeometry,
  CylinderGeometry,
  PlaneGeometry,
  MeshStandardMaterial,
  AmbientLight,
  DirectionalLight,
  HemisphereLight,
  TextureLoader,
  Texture
} from 'three';
import { ScratchcardGameSettings } from '@kanek/shared-data-access';
import { ScratchcardRenderer } from '../plugins/scratchcard/scratchcard-renderer';
import { calculateHTMLDimensions3D } from '../utils/three-scene.utils';

// Extend Three.js objects for Angular Three
extend({
  BoxGeometry,
  CylinderGeometry,
  PlaneGeometry,
  MeshStandardMaterial,
  AmbientLight,
  DirectionalLight,
  HemisphereLight
});

/**
 * Scratchcard Scene Component
 *
 * Declarative Angular Three component that renders the complete scratchcard game in 3D:
 * - Rectangular card base (thin 3D lottery ticket)
 * - Header area at top (for branding/logo)
 * - HTML scratchcard overlay (projected onto card face)
 *
 * Architecture:
 * - Pure presentation component (stateless)
 * - All state passed via @Input() or scene.userData
 * - Reactive geometry using computed signals
 * - Follows Angular Three / React Three Fiber patterns
 *
 * Design:
 * - Portrait orientation (vertical, phone-friendly)
 * - Card is slightly wider than scratchcard grid
 * - Header area at top (15-20% of card height)
 * - Thin depth (like physical lottery ticket)
 * - Supports custom header image upload
 *
 * Usage:
 * ```html
 * <ngt-canvas>
 *   <lib-scratchcard-scene
 *     [settings]="scratchcardSettings"
 *     [scratchcardContainer]="container" />
 * </ngt-canvas>
 * ```
 */
@Component({
  selector: 'lib-scratchcard-scene',
  standalone: true,
  imports: [NgtsHTML, NgtsHTMLContent, NgtsOrbitControls, NgtArgs],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <!-- OrbitControls (conditional on enableCameraControls from userData) -->
    @if (enableCameraControls()) {
      <ngts-orbit-controls
        [options]="{
          enableDamping: true,
          dampingFactor: 0.05,
          enablePan: true
        }" />
    }

    <!-- Three-Point Lighting System -->

    <!-- Ambient: Base illumination -->
    <ngt-ambient-light [intensity]="0.8" />

    <!-- Hemisphere: Natural sky/ground gradient lighting -->
    <ngt-hemisphere-light
      [skyColor]="16777215"
      [groundColor]="4473924"
      [intensity]="0.5"
    />

    <!-- Key Light: Main directional (strongest, casts shadows) -->
    <ngt-directional-light
      [position]="[5, 8, 5]"
      [intensity]="2.5"
      [castShadow]="true"
    />

    <!-- Fill Light: Softer secondary (reduces harsh shadows) -->
    <ngt-directional-light
      [position]="[-3, 4, -2]"
      [intensity]="0.8"
    />

    <!-- Rim Light: Back-highlight (adds depth and separation) -->
    <ngt-directional-light
      [position]="[0, 3, -6]"
      [intensity]="1.0"
    />

    <!-- Scene Group (rotated for AR mode) -->
    <ngt-group [rotation]="sceneGroupRotation()">
      <!-- Card Border/Rim (behind main card for decorative edge) -->
      @if (cardBorderSize() > 0) {
        <ngt-mesh
          [position]="[0, 0, -0.02]"
          [castShadow]="castShadow()">
          <ngt-value [rawValue]="cardBorderGeometry()" attach="geometry" />
          <ngt-value [rawValue]="cardBorderMaterial()" attach="material" />
        </ngt-mesh>
      }

      <!-- Rectangular Card Base (lottery scratch card backing) -->
      <ngt-mesh
        [castShadow]="castShadow()"
        [receiveShadow]="receiveShadow()">
        <ngt-value [rawValue]="cardGeometry()" attach="geometry" />
        <ngt-value [rawValue]="cardMaterial()" attach="material" />
      </ngt-mesh>

      <!-- Header Section (branding area at top of card) -->
      @if (headerEnabled()) {
        <ngt-mesh
          [position]="headerPosition()"
          [castShadow]="false">
          <ngt-value [rawValue]="headerGeometry()" attach="geometry" />
          <ngt-value [rawValue]="headerMaterial()" attach="material" />
        </ngt-mesh>
      }

      <!-- Footer Section (info area at bottom of card) -->
      @if (footerEnabled()) {
        <ngt-mesh
          [position]="footerPosition()"
          [castShadow]="false">
          <ngt-value [rawValue]="footerGeometry()" attach="geometry" />
          <ngt-value [rawValue]="footerMaterial()" attach="material" />
        </ngt-mesh>
      }

      <!-- Reflective Overlay Mesh (glass-like with reflections) -->
      <!-- TEST: Check if this blocks scratchcard UI interactions -->
      <ngt-mesh
        [position]="htmlReflectiveOverlayPosition()"
        [scale]="htmlReflectiveOverlayScale()"
        [receiveShadow]="true"
        [castShadow]="false">
        <ngt-plane-geometry *args="[1, 1]" />
        <ngt-mesh-standard-material
          [transparent]="true"
          [opacity]="0.2"
          [roughness]="0.0"
          [metalness]="1.0"
          [side]="FrontSide"
          [depthWrite]="false"
          [blending]="Blending"
        />
      </ngt-mesh>

      <!-- HTML Scratchcard Overlay -->
      <ngts-html [options]="htmlOverlayOptions()">
        <!-- Occlusion geometry: matches scratchcard size -->
        <ngt-plane-geometry
          data-occlusion-geometry
        />
        <!-- Custom occlusion material to avoid shader compilation errors -->
        <ngt-mesh-basic-material
          data-occlusion-material
          [colorWrite]="false"
        />
        <div [ngtsHTMLContent]="htmlContentOptions()">
          <div #scratchcardHost
               [style.width]="scratchcardDimensions().width"
               [style.height]="scratchcardDimensions().height">
          </div>
        </div>
      </ngts-html>
    </ngt-group>
  `,
  styles: [`
    :host {
      display: contents;
    }
  `]
})
export class ScratchcardSceneComponent {
  // Inputs for direct usage (e.g., playground)
  settings = input<ScratchcardGameSettings | null>(null);
  scratchcardContainer = input<HTMLElement | null>(null);

  // Signal from game-engine to notify when renderer is configured with winning prize
  rendererReady = input<Signal<number> | undefined>(undefined);

  // ViewChild to get the rendered scratchcard host element
  private scratchcardHost = viewChild<ElementRef<HTMLDivElement>>('scratchcardHost');

  // ViewChild to get OrbitControls instance (when enabled)
  private orbitControlsRef = viewChild(NgtsOrbitControls);

  private store = injectStore();
  private arService = inject(ArService);
  private confettiService = inject(ConfettiService);
  private audioService = inject(AudioService);
  private textureLoader = new TextureLoader();
  private headerTexture: Texture | null = null;
  
  // Store renderer instance to enable game reset
  private scratchcardRenderer: ScratchcardRenderer | null = null;

  // Computed signals that read from scene.userData when available (sceneGraph usage)
  // or fall back to inputs (direct usage like playground)
  protected settingsData = computed(() => {
    const inputSettings = this.settings();
    
    // Read configSignal from scene.userData and CALL it to track changes (reactive!)
    const scene = this.store.get().scene;
    const configSignal = scene?.userData?.['configSignal'];
    const config = configSignal ? configSignal() : null;
    const sceneSettings = config?.['settings'];
    
    return inputSettings || sceneSettings || null;
  });

  // Computed signal for camera controls flag from userData
  protected enableCameraControls = computed(() => {
    const scene = this.store.get().scene;
    const fromUserData = scene?.userData?.['enableCameraControls'] ?? false;

    // Enable OrbitControls when AR bypass is active (preview mode)
    const bypassActive = this.arService.shouldBypassAR;
    return bypassActive || fromUserData;
  });

  // Computed signal for AR mode flag from userData
  protected enableAR = computed(() => {
    const scene = this.store.get().scene;
    const fromUserData = scene?.userData?.['enableAR'] ?? false;

    // Disable AR rotation when bypass is active (render upright like preview)
    const bypassActive = this.arService.shouldBypassAR;
    return fromUserData && !bypassActive;
  });

  // Computed scene group rotation based on AR mode
  // In AR mode, rotate to make scene lie flat on marker
  protected sceneGroupRotation = computed(() => {
    const arMode = this.enableAR();
    return arMode ? [-Math.PI / 2, 0, 0] : [0, 0, 0];
  });

  // Shadow settings
  protected castShadow = computed(() => {
    return this.settingsData()?.castShadow ?? true;
  });

  protected receiveShadow = computed(() => {
    return this.settingsData()?.receiveShadow ?? true;
  });

  // Computed scratchcard dimensions
  protected scratchcardDimensions = computed(() => {
    const settings = this.settingsData();
    return {
      width: `${settings?.containerWidth ?? 400}px`,
      height: `${settings?.containerHeight ?? 600}px`
    };
  });

  // Computed items data from scene.userData or input (prize items from campaign)
  protected itemsData = computed(() => {
    // Read configSignal from scene.userData and CALL it to track changes (reactive!)
    const scene = this.store.get().scene;
    const configSignal = scene?.userData?.['configSignal'];
    const config = configSignal ? configSignal() : null;
    const items = config?.['items'];
    return items || [];
  });

  // Computed: Base HTML dimensions converted to 3D units (calculated once, reused everywhere)
  // Uses shared utility for consistent conversion across all scene types
  protected htmlDimensions3D = computed<{ width: number; height: number }>(() => {
    const settings = this.settingsData();
    const containerWidth = settings?.containerWidth || 500;
    const containerHeight = settings?.containerHeight || 500;

    return calculateHTMLDimensions3D(containerWidth, containerHeight, this.store.get());
  });

  /**
   * Reflective overlay position (slightly in front of HTML)
   */
  protected htmlReflectiveOverlayPosition = computed<[number, number, number]>(() => {
    const cardDepth = this.cardDepth();
    const headerHeight = this.headerHeight();
    const footerHeight = this.footerHeight();
    const htmlZOffset = cardDepth / 2 + 0.01;
    const htmlYOffset = (footerHeight - headerHeight) / 2;
    // Position overlay 0.01 units in front of HTML
    return [0, htmlYOffset, htmlZOffset + 0.01];
  });

  /**
   * Reflective overlay scale (matches HTML scratchcard exactly)
   */
  protected htmlReflectiveOverlayScale = computed<[number, number, number]>(() => {
    // Use corrected HTML dimensions directly (1% correction already applied in htmlDimensions3D)
    const dimensions = this.htmlDimensions3D();
    return [dimensions.width, dimensions.height, 1];
  });

  constructor() {
    // Store OrbitControls reference in scene.userData when ready
    effect(() => {
      const controls = this.orbitControlsRef();
      const scene = this.store.get().scene;
      if (controls && scene) {
        const orbitControls = controls.controls();
        scene.userData['orbitControls'] = orbitControls;
      }
    });

    // Initialize scratchcard when scratchcardHost is ready (for GameEngine sceneGraph usage)
    effect(() => {
      const host = this.scratchcardHost();
      const settings = this.settingsData();
      const items = this.itemsData();
      
      // Get scene to check for renderer and config
      const scene = this.store.get().scene;
      const sharedRenderer = scene?.userData?.['renderer'] as ScratchcardRenderer | undefined;
      
      // Track configSignal to detect when Prize API completes
      const configSignal = scene?.userData?.['configSignal'];
      const config = configSignal && typeof configSignal === 'function' ? configSignal() : null;
      const prizeApiComplete = config?._prizeApiComplete;

      // Check if reset was requested
      const resetRequested = (settings as any)?._resetRequested;

      // Only initialize if host and settings are ready
      if (!host?.nativeElement || !settings) {
        return;
      }

      // Items can be empty in preview mode - renderer will use defaults
      const effectiveItems = items && items.length > 0 ? items : [];

      // If reset was requested, destroy existing renderer
      if (resetRequested && this.scratchcardRenderer) {
        console.log('[ScratchcardScene] Reset requested - destroying existing renderer');
        this.scratchcardRenderer.destroy();
        this.scratchcardRenderer = null;
      }

      // If renderer already exists, update its configuration instead of re-initializing
      if (this.scratchcardRenderer) {
        console.log('[ScratchcardScene] Renderer exists - updating configuration');
        this.initializeScratchcard(host.nativeElement, settings, effectiveItems);
        return;
      }

      // Check if we have a shared renderer AND it's ready
      // For Experience App: wait for prizeApiComplete flag (set after Prize API)
      // For Portal Preview: no Prize API expected, so proceed immediately
      const expectsPrizeApi = scene?.userData?.['_expectsPrizeApi'] === true;
      const isReadyToInitialize = sharedRenderer && (prizeApiComplete === true || !expectsPrizeApi);

      if (isReadyToInitialize) {
        // Renderer is ready now - initialize immediately
        this.scratchcardRenderer = sharedRenderer!;
        this.initializeScratchcard(host.nativeElement, settings, effectiveItems);

        // Store resetGame method in scene.userData
        if (scene) {
          scene.userData['resetGame'] = () => this.resetGame();
        }
      } else if (!sharedRenderer) {
        // Renderer not ready yet - schedule a retry after microtask
        // This handles the timing issue where game-engine hasn't set renderer yet
        Promise.resolve().then(() => {
          // Re-check for renderer after game-engine effect runs
          const retryScene = this.store.get().scene;
          const retryRenderer = retryScene?.userData?.['renderer'] as ScratchcardRenderer | undefined;
          
          // Check if Prize API is expected
          const retryExpectsPrizeApi = retryScene?.userData?.['_expectsPrizeApi'] === true;
          
          // Also re-check Prize API status if it's expected
          const retryConfigSignal = retryScene?.userData?.['configSignal'];
          const retryConfig = retryConfigSignal && typeof retryConfigSignal === 'function' ? retryConfigSignal() : null;
          const retryPrizeApiComplete = retryConfig?._prizeApiComplete;
          
          // Only initialize if renderer exists AND either:
          // - Prize API is NOT expected (Portal), OR
          // - Prize API IS expected AND completed (Experience App)
          const retryIsReady = retryRenderer && (retryPrizeApiComplete === true || !retryExpectsPrizeApi);
          
          if (retryIsReady && !this.scratchcardRenderer) {
            console.log('[ScratchcardScene] Renderer found on retry - initializing');
            this.scratchcardRenderer = retryRenderer;
            this.initializeScratchcard(host.nativeElement, settings, effectiveItems);
            
            if (retryScene) {
              retryScene.userData['resetGame'] = () => this.resetGame();
            }
          }
          // If not ready yet (Experience App waiting for Prize API), effect will re-run when prizeApiComplete changes
        });
      }
    });

    // Support for playground mode: append pre-initialized container if provided
    effect(() => {
      const host = this.scratchcardHost();
      const container = this.scratchcardContainer(); // From input

      if (host && container && host.nativeElement) {
        host.nativeElement.appendChild(container);
      }
    });

    // Load header texture when URL changes
    effect(() => {
      const settings = this.settingsData();
      const headerImageUrl = settings?.cardHeaderImageUrl;

      if (headerImageUrl) {
        this.textureLoader.load(
          headerImageUrl,
          (texture) => {
            this.headerTexture = texture;
          },
          undefined,
          (error) => {
            console.error('[ScratchcardScene] Failed to load header image:', error);
            this.headerTexture = null;
          }
        );
      } else {
        this.headerTexture = null;
      }
    });
  }

  /**
   * Initialize scratchcard directly in the scratchcardHost div
   * Used when ScratchcardScene is loaded via GameEngine sceneGraph
   */
  private async initializeScratchcard(
    container: HTMLElement,
    settings: ScratchcardGameSettings,
    items: Array<{
      id?: string;
      name?: string;
      label?: string;
      probability?: number;
      value?: string;
      [key: string]: unknown
    }>
  ): Promise<void> {
    try {

      // Explicitly set container size based on settings
      const width = settings.containerWidth || 400;
      const height = settings.containerHeight || 600;
      const size = `${width}px`;
      const heightSize = `${height}px`;

      container.style.width = size;
      container.style.height = heightSize;

      // Renderer should already be created by the effect
      if (!this.scratchcardRenderer) {
        console.error('[ScratchcardScene] Renderer not created - this should not happen');
        return;
      }
      
      // Pass services for effects and AR keepVisible flag
      this.scratchcardRenderer.initialize(container, this.confettiService, this.audioService, this.arService);

      // Convert items to prize format
      // Items can be either Prize objects (with id, name, probability) or simplified wheel items (with label)
      // If no items provided, create default preview prize
      const prizes = items.length > 0 
        ? items.map((item, index) => ({
            id: item.id || `${index + 1}`,
            name: item.name || item.label || `Prize ${index + 1}`,
            value: item.value || item.name || item.label || `Prize ${index + 1}`,
            probability: item.probability ?? (1 / items.length) // Use actual probability or equal distribution
          }))
        : [
            // Default preview prizes when no items configured
            { id: '1', name: 'Preview Prize', value: 'You Win!', probability: 1.0 }
          ];

      // Configure renderer to create the grid
      // NOTE: If game-engine already set winningPrize via Prize API, renderer will preserve it
      // If not (portal preview), renderer will randomly select one
      await this.scratchcardRenderer.updateConfig({
        ...settings,
        _previewPrizes: prizes,
        _onComplete: () => {
          // Get AppStore from scene.userData (injected by game-engine)
          const scene = this.store.get().scene;
          const appStore = scene?.userData?.['appStore'];
          
          if (appStore) {
            // Mark prize as revealed (enables navigation)
            const session = appStore.session();
            appStore.setSession({
              ...session,
              prizeRevealed: true
            });
          } else {
            console.warn('[ScratchcardScene] AppStore not found in scene.userData');
          }
        }
      });

      // Store resetGame method in scene.userData
      const sceneForReset = this.store.get().scene;
      if (sceneForReset) {
        sceneForReset.userData['resetGame'] = () => this.resetGame();
      }
    } catch (error) {
      console.error('[ScratchcardScene] Failed to initialize scratchcard:', error);
    }
  }

  protected readonly Math = Math;
  protected readonly FrontSide = THREE.FrontSide;
  protected readonly Blending = THREE.AdditiveBlending;

  // ============================================================================
  // Card Geometry (Rectangular lottery scratch card base)
  // ============================================================================

  /**
   * Header height as percentage of scratchcard area (0-1)
   */
  protected headerHeightPercent = computed(() => {
    const settings = this.settingsData();
    return settings?.cardHeaderHeightPercent ?? 0.5; // Default 50% for header
  });

  /**
   * Footer height as percentage of scratchcard area (0-1)
   */
  protected footerHeightPercent = computed(() => {
    const settings = this.settingsData();
    return settings?.cardFooterHeightPercent ?? 0.1; // Default 10% for footer
  });

  /**
   * Calculate card width from scratchcard container width
   * Uses cached HTML dimensions for accurate conversion
   */
  protected cardWidth = computed(() => {
    const dimensions = this.htmlDimensions3D();
    return dimensions.width;
  });

  /**
   * Height of the scratchcard play area (in 3D units)
   * Uses cached HTML dimensions for accurate conversion
   */
  protected scratchcardAreaHeight = computed(() => {
    const dimensions = this.htmlDimensions3D();
    return dimensions.height;
  });

  /**
   * Height of the header section (in 3D units)
   */
  protected headerHeight = computed(() => {
    const scratchHeight = this.scratchcardAreaHeight();
    const headerPercent = this.headerHeightPercent();
    return scratchHeight * headerPercent;
  });

  /**
   * Height of the footer section (in 3D units)
   */
  protected footerHeight = computed(() => {
    const scratchHeight = this.scratchcardAreaHeight();
    const footerPercent = this.footerHeightPercent();
    return scratchHeight * footerPercent;
  });

  /**
   * Total card height = scratchcard area + header section + footer section
   * Portrait orientation for lottery card appearance
   */
  protected cardHeight = computed(() => {
    const scratchHeight = this.scratchcardAreaHeight();
    const headerHeight = this.headerHeight();
    const footerHeight = this.footerHeight();
    return scratchHeight + headerHeight + footerHeight;
  });

  /**
   * Card depth (thickness of the lottery card)
   */
  protected cardDepth = computed(() => {
    const settings = this.settingsData();
    return settings?.cardDepth ?? 0.1; // Thin card by default
  });

  protected cardGeometry = computed(() => {
    const width = this.cardWidth();
    const height = this.cardHeight();
    const depth = this.cardDepth();
    return new BoxGeometry(width, height, depth);
  });

  protected cardMaterial = computed(() => {
    const settings = this.settingsData();
    return new MeshStandardMaterial({
      color: settings?.card3DColor ?? '#ffffff',
      transparent: settings?.cardOpacity !== undefined && settings.cardOpacity < 1,
      opacity: settings?.cardOpacity ?? 1.0,
      roughness: settings?.cardRoughness ?? 0.5,
      metalness: settings?.cardMetalness ?? 0.0
    });
  });

  // ============================================================================
  // Card Border/Rim (Decorative edge around card)
  // ============================================================================

  /**
   * Border size in 3D units
   */
  protected cardBorderSize = computed(() => {
    const settings = this.settingsData();
    const marginPercent = settings?.cardMarginPercent ?? 0.05; // Default 5% margin
    const width = this.cardWidth();
    return width * marginPercent;
  });

  protected cardBorderGeometry = computed(() => {
    const width = this.cardWidth();
    const height = this.cardHeight();
    const borderSize = this.cardBorderSize();

    // Border is slightly larger than main card
    const borderWidth = width + (borderSize * 2);
    const borderHeight = height + (borderSize * 2);
    const borderDepth = this.cardDepth() * 0.8; // Slightly thinner than main card

    return new BoxGeometry(borderWidth, borderHeight, borderDepth);
  });

  protected cardBorderMaterial = computed(() => {
    const settings = this.settingsData();
    return new MeshStandardMaterial({
      color: settings?.cardBorderColor ?? '#ffd700', // Gold border by default
      roughness: 0.3,
      metalness: 0.7 // Metallic border
    });
  });

  // ============================================================================
  // Header Section (Branding area at top of card)
  // ============================================================================

  /**
   * Whether header section is enabled
   */
  protected headerEnabled = computed(() => {
    const settings = this.settingsData();
    return settings?.cardHeaderEnabled ?? true;
  });

  protected headerGeometry = computed(() => {
    const width = this.cardWidth();
    const height = this.headerHeight();
    const depth = 0.02; // Very thin plane for header
    return new BoxGeometry(width, height, depth);
  });

  protected headerMaterial = computed(() => {
    const settings = this.settingsData();
    return new MeshStandardMaterial({
      color: settings?.cardHeaderColor ?? '#ed1f24',
      roughness: 0.4,
      metalness: 0.1
    });
  });

  /**
   * Position of header section (top of card)
   */
  protected headerPosition = computed((): [number, number, number] => {
    const scratchHeight = this.scratchcardAreaHeight();
    const footerHeight = this.footerHeight();

    // Position header at top of card
    // Y offset = (scratchcard height / 2) + (footer height / 2) to account for footer
    const yOffset = (scratchHeight / 2) + (footerHeight / 2);
    const zOffset = this.cardDepth() / 2 + 0.02; // In front of card

    return [0, yOffset, zOffset];
  });

  // ============================================================================
  // Footer Section (Info area at bottom of card)
  // ============================================================================

  /**
   * Whether footer section is enabled
   */
  protected footerEnabled = computed(() => {
    const settings = this.settingsData();
    return settings?.cardFooterEnabled ?? true;
  });

  protected footerGeometry = computed(() => {
    const width = this.cardWidth();
    const height = this.footerHeight();
    const depth = 0.02; // Very thin plane for footer
    return new BoxGeometry(width, height, depth);
  });

  protected footerMaterial = computed(() => {
    const settings = this.settingsData();
    return new MeshStandardMaterial({
      color: settings?.cardFooterColor ?? '#333333',
      roughness: 0.4,
      metalness: 0.1
    });
  });

  /**
   * Position of footer section (bottom of card)
   */
  protected footerPosition = computed((): [number, number, number] => {
    const scratchHeight = this.scratchcardAreaHeight();
    const headerHeight = this.headerHeight();

    // Position footer at bottom of card
    // Y offset = -(scratchcard height / 2) - (header height / 2) to move to bottom edge
    const yOffset = -(scratchHeight / 2) - (headerHeight / 2);
    const zOffset = this.cardDepth() / 2 + 0.02; // In front of card

    return [0, yOffset, zOffset];
  });

  // ============================================================================
  // HTML Overlay (Scratchcard projected onto card face)
  // ============================================================================

  protected htmlOverlayOptions = computed((): Partial<NgtsHTMLOptions> => {
    const cardDepth = this.cardDepth();
    const headerHeight = this.headerHeight();
    const footerHeight = this.footerHeight();

    // Z offset: Position HTML just in front of card face
    const htmlZOffset = cardDepth / 2 + 0.01;

    // Y offset: Center scratchcard between header and footer
    // Shift down by (header - footer) / 2
    const htmlYOffset = (footerHeight - headerHeight) / 2;

    return {
      occlude: 'blending',
      transform: true,
      scale: 1.0, // Always 1.0 to maintain visual alignment with 3D geometry
      position: [0, htmlYOffset, htmlZOffset] as [number, number, number],
      rotation: [0, 0, 0] as [number, number, number]  // No rotation needed
    };
  });

  protected htmlContentOptions = computed(() => ({
    distanceFactor: 0,  // HTML at pixel size, 3D uses unit-based dimensions
    zIndexRange: [100, 1] as [number, number]  // CTA button at 101 ensures it appears on top
  }));

  /**
   * Get OrbitControls instance (for ExperiencePreview to manipulate)
   * Returns null if controls are not enabled
   */
  public getOrbitControls(): object | null {
    return this.orbitControlsRef()?.controls() ?? null;
  }

  /**
   * Reset the scratchcard game state
   * Called by refresh button in Experience Studio
   */
  resetGame(): void {
    console.log('[ScratchcardScene] Resetting scratchcard game - destroying and recreating renderer');
    
    // Destroy existing renderer to reset game state
    if (this.scratchcardRenderer) {
      this.scratchcardRenderer.destroy();
      this.scratchcardRenderer = null;
    }
    
    // Trigger re-initialization
    const host = this.scratchcardHost();
    const settings = this.settingsData();
    const items = this.itemsData();
    
    if (host?.nativeElement && settings && items && items.length > 0) {
      console.log('[ScratchcardScene] Reinitializing scratchcard after reset');
      this.initializeScratchcard(host.nativeElement, settings, items);
    }
  }
}
